import { useTranslation } from 'react-i18next';

import { useDisplacementSummaryQuery } from '@/hooks/displacementSummary';
import { Spinner } from '@/pages/LoadingPage';

import type { DisplacedSummaryItem } from '@/service/types/displacementSummaryTypes';

// ─────────────────────────────────────────────────────────────────
// Date Formatter — Matches Xamarin DisplacedDateTimeFormatConverter
// Format: MM/dd/yyyy hh:mm AM/PM (uppercased)
// ─────────────────────────────────────────────────────────────────

function formatDisplacedDateTime(dateStr: string): string {
  if (!dateStr || dateStr.trim() === '') return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear());
    let hours = date.getHours();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    const hoursStr = String(hours).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');

    return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
  } catch {
    return dateStr;
  }
}

// ─────────────────────────────────────────────────────────────────
// Table Header — Matches Xamarin Frame with TrailerHeaderBackgroundColor (#D7E7F3)
// Columns: Trailer Number, Current Location, Last Known From Location, Time of Last Moved
// Style: CardTitleLabelStyle — PoppinsBold, Phone=12px, TextColor=PrimaryBackgroundColor (#003366)
// ─────────────────────────────────────────────────────────────────

function DisplacementTableHeader() {
  const { t } = useTranslation();
  return (
    <div className="min-w-fit">
      <div className="grid min-h-[50px] w-full min-w-[700px] grid-cols-[1fr_1fr_1.4fr_1.3fr] items-center gap-2 bg-[#D7E7F3] px-4 py-2">
        <span className="font-poppins text-[12px] font-bold text-[#003366]">
          {t('displacement.trailerNumber')}
        </span>
        <span className="font-poppins text-[12px] font-bold text-[#003366]">
          {t('displacement.currentLocation')}
        </span>
        <span className="font-poppins text-[12px] font-bold text-[#003366]">
          {t('displacement.previousLocation')}
        </span>
        <span className="font-poppins text-[12px] font-bold text-[#003366]">
          {t('displacement.timeOfMoved')}
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Table Row — Matches Xamarin CollectionView.ItemTemplate
// Style: CardValueLabelStyle — PoppinsRegular, Phone=12px, TextColor=TaskDetailsTextColor (#243743)
// Separator: BoxView HeightRequest=1, BackgroundColor=TaskSeperationLineBackgroundColor (#C3D2DC)
// ─────────────────────────────────────────────────────────────────

interface DisplacementRowProps {
  item: DisplacedSummaryItem;
  isLast: boolean;
}

function DisplacementRow({ item, isLast }: DisplacementRowProps) {
  return (
    <div className="px-[10px] pt-0 pb-[10px]">
      <div className="grid w-full min-w-[700px] grid-cols-[1fr_1fr_1.4fr_1.3fr] items-center gap-2 pt-[10px]">
        <span className="font-poppins text-[12px] text-task-details">{item.TrailerName}</span>
        <span className="font-poppins text-[12px] text-task-details">{item.CurrentLocation}</span>
        <span className="font-poppins text-[12px] text-task-details">{item.PreviousLocation}</span>
        <span className="font-poppins text-[12px] text-task-details">
          {formatDisplacedDateTime(item.TimeOfLastMoved)}
        </span>
      </div>
      {/* Separator — Matches Xamarin BoxView */}
      {!isLast && <div className="mt-[5px] mr-[5px] mb-[12px] h-[1px] bg-task-separation" />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Empty State — Matches Xamarin empty list with "No Trailer" illustration
// Full-height illustration centered below the table header
// ─────────────────────────────────────────────────────────────────

function EmptyDisplacementState() {
  return (
    <div className="flex flex-1 items-center justify-center">
      <img
        src="/images/empty_trailer_illustration.svg"
        alt="No Trailer"
        className="max-h-[calc(100vh-220px)] w-auto max-w-full object-contain"
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Page Component — Matches Xamarin DisplaceSummaryPageViewModel
// ─────────────────────────────────────────────────────────────────

export function DisplacementSummaryPage() {
  const { data, isLoading, isSuccess } = useDisplacementSummaryQuery();

  const items = data?.DisplacedSummaryModelList ?? [];

  return (
    // Matches Xamarin: Grid Padding="16,22,16,22", full height layout
    <div className="flex flex-col px-4 py-[22px] h-[calc(100vh-64px)]">
      <div className="flex flex-col flex-1 min-h-0">
        {/* Table Header */}
        <DisplacementTableHeader />

        {/* Table Body — Matches Xamarin CollectionView with Margin="0,10,0,0" */}
        <div className="mt-[10px] flex-1 flex flex-col min-h-0">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner size="lg" />
            </div>
          ) : !isSuccess || items.length === 0 ? (
            <EmptyDisplacementState />
          ) : (
            items.map((item, index) => (
              <DisplacementRow
                key={item.TrailerId || index}
                item={item}
                isLast={index === items.length - 1}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}
